import React, { useEffect, useState, useMemo, useCallback } from "react";
import toast from "react-hot-toast";
import { UserCheck, History, User, Shield, UserPlus } from "lucide-react";
import { useUserDashboard } from "../../hooks/dashboard/useUserDashboard";
import { useDebounce } from "../../hooks/common/useDebounce";
import CommonSidebar from "../../components/dashboard/CommonSidebar";
import HistoryModal from "../../components/dashboard/HistoryModal";
import ConfirmModal from "../../components/dashboard/ConfirmModal";
import DataTable from "../../components/common/DataTable";
import UserSearchBar from "../../components/management/UserSearchBar";
import UserCreateModal from "../../components/dashboard/UserCreateModal";
import { DataTableColumn, PaginationInfo } from "../../types/dataTable";
import { UserData } from "../../types/dashboard";

export default function UserManagement() {
  const {
    error,
    isLoading,
    isHistoryLoading,
    isEmpty,
    userList,
    totalUserCount,
    userHistory,
    selectedUserId,
    getUserDataList,
    getUserSearch,
    getUserHistory,
    userRankUpdate,
    updateUserRankInState,
    clearSelectedUser,
    createUser,
  } = useUserDashboard();

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  // 확인 모달 관련 상태
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    userId: "",
    currentRank: "",
    newRank: "",
    userName: "",
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // 페이지네이션 계산
  const totalPages = Math.ceil(totalUserCount / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + userList.length, totalUserCount);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (debouncedSearchTerm.trim()) {
      getUserSearch(debouncedSearchTerm, currentPage);
    } else {
      getUserDataList(currentPage);
    }
  }, [currentPage, debouncedSearchTerm, getUserDataList, getUserSearch]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setCurrentPage(1);
      if (searchTerm.trim()) {
        getUserSearch(searchTerm, 1);
      } else {
        getUserDataList(1);
      }
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    getUserDataList(1);
  };

  const getGenderDisplay = (gender: string) => {
    return gender === "m" ? "남성" : gender === "f" ? "여성" : "미정";
  };

  const getPlatformDisplay = (platform: string) => {
    const platformMap: Record<string, string> = {
      google: "Google",
      kakao: "Kakao",
      naver: "Naver",
      aivy: "Aivy",
    };
    return platformMap[platform.toLowerCase()] || platform;
  };

  const getEmailDisplay = (email: string) => {
    if (
      !email ||
      email === "unknown" ||
      email === "unknwon" ||
      email.trim() === ""
    ) {
      return "-";
    }
    return email;
  };

  // 등급 업데이트 확인 모달 열기
  const handleGradeUpdateClick = useCallback(
    (userId: string, sig_typ: string, userName: string) => {
      const newRank = sig_typ === "user" ? "expert" : "user";

      setConfirmModal({
        isOpen: true,
        userId,
        currentRank: sig_typ,
        newRank,
        userName,
      });
    },
    []
  );

  // 등급 업데이트 실행
  const handleConfirmGradeUpdate = async () => {
    setIsUpdating(true);

    try {
      const success = await userRankUpdate(
        confirmModal.userId,
        confirmModal.newRank
      );

      if (success) {
        const rankText =
          confirmModal.newRank === "expert" ? "전문가" : "일반 사용자";
        updateUserRankInState(confirmModal.userId, confirmModal.newRank);

        // 성공 알림 토스트 메시지로 변경 예정
        toast.success(`사용자 등급이 ${rankText}로 변경되었습니다.`);
      } else {
        toast.error("등급 변경에 실패했습니다. 다시 시도해주세요.");
      }
    } catch (error) {
      toast.error("등급 변경 중 오류가 발생했습니다.");
    } finally {
      setIsUpdating(false);
      setConfirmModal({
        isOpen: false,
        userId: "",
        currentRank: "",
        newRank: "",
        userName: "",
      });
    }
  };

  // 확인 모달 닫기
  const handleCloseConfirmModal = () => {
    if (!isUpdating) {
      setConfirmModal({
        isOpen: false,
        userId: "",
        currentRank: "",
        newRank: "",
        userName: "",
      });
    }
  };

  // 히스토리 보기 함수 - 모달 후 Aivy Home형식
  const handleViewHistory = useCallback(
    (userId: string) => {
      getUserHistory(userId, 1);
    },
    [getUserHistory]
  );

  const handleCloseModal = () => {
    clearSelectedUser();
  };

  const columns: DataTableColumn<UserData>[] = useMemo(
    () => [
      {
        key: "nickname",
        header: "이름",
        render: (value) => (
          <div className="flex items-center gap-3">
            <span className="text-white font-medium">{value || "-"}</span>
          </div>
        ),
      },
      {
        key: "gender",
        header: "성별",
        render: (value) => (
          <span className="text-slate-300">
            {getGenderDisplay(value || "")}
          </span>
        ),
      },
      {
        key: "birth",
        header: "생년월일",
        render: (value) => (
          <span className="text-slate-300">{value || "-"}</span>
        ),
      },
      {
        key: "contact_hp",
        header: "연락처",
        render: (value) => (
          <span className="text-slate-300">{value || "-"}</span>
        ),
      },
      {
        key: "platform",
        header: "플랫폼",
        render: (value) => (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              value?.toLowerCase() === "aivy"
                ? "bg-purple-500/20 text-purple-400"
                : value?.toLowerCase() === "kakao"
                ? "bg-yellow-500/20 text-yellow-400"
                : value?.toLowerCase() === "google"
                ? "bg-red-500/20 text-red-400"
                : value?.toLowerCase() === "naver"
                ? "bg-green-500/20 text-green-400"
                : "bg-blue-500/20 text-blue-400"
            }`}
          >
            {getPlatformDisplay(value || "")}
          </span>
        ),
      },
      {
        key: "email",
        header: "이메일",
        render: (value) => (
          <span className="text-slate-300">{getEmailDisplay(value || "")}</span>
        ),
      },
      {
        key: "sig_typ",
        header: "계정 등급",
        render: (value) => (
          <span
            className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
              value === "expert"
                ? "bg-amber-500/20 text-amber-400"
                : "bg-slate-600/20 text-slate-400"
            }`}
          >
            {value === "expert" ? (
              <>
                <Shield className="w-3 h-3" />
                전문가
              </>
            ) : (
              <>
                <User className="w-3 h-3" />
                일반
              </>
            )}
          </span>
        ),
      },
      {
        key: "actions",
        header: "관리",
        render: (_, user) => (
          <div className="flex items-center gap-2">
            <button
              onClick={() =>
                handleGradeUpdateClick(
                  user.id,
                  user.sig_typ || "",
                  user.nickname || ""
                )
              }
              className="p-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-colors cursor-pointer"
              title="등급 변경"
            >
              <UserCheck className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleViewHistory(user.id)}
              className="p-2 bg-slate-700 text-slate-400 rounded-lg hover:bg-slate-600 transition-colors"
              title="히스토리 보기"
            >
              <History className="w-4 h-4" />
            </button>
          </div>
        ),
      },
    ],
    [handleGradeUpdateClick, handleViewHistory]
  );

  const paginationInfo: PaginationInfo = {
    currentPage,
    totalPages,
    totalCount: totalUserCount,
    startIndex,
    endIndex,
    onPageChange: setCurrentPage,
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 lg:ml-72">
        <div className="bg-slate-900 border-b border-slate-800 p-6">
          <h1 className="text-2xl font-bold text-white">사용자 관리</h1>
        </div>
        <CommonSidebar />
        <div className="p-6">
          <UserSearchBar
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            onKeyPress={handleKeyPress}
            onClearSearch={handleClearSearch}
            totalUserCount={totalUserCount}
            debouncedSearchTerm={debouncedSearchTerm}
          />
          <div
            className={`bg-slate-900 rounded-xl border border-slate-800 overflow-hidden p-6`}
          >
            <div className="flex justify-center items-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent"></div>
              <span className="ml-3 text-slate-400">
                데이터를 불러오는 중...
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="min-h-screen bg-slate-950 lg:ml-72">
        <div className="bg-slate-900 border-b border-slate-800 p-6">
          <h1 className="text-2xl font-bold text-white">사용자 관리</h1>
        </div>
        <CommonSidebar />
        <div className="p-6">
          <UserSearchBar
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            onKeyPress={handleKeyPress}
            onClearSearch={handleClearSearch}
            totalUserCount={totalUserCount}
            debouncedSearchTerm={debouncedSearchTerm}
          />

          <DataTable<UserData>
            columns={columns}
            data={userList}
            pagination={paginationInfo}
            loading={false}
            error={null}
            emptyMessage="등록된 사용자가 없습니다."
          />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 lg:ml-72">
        <div className="bg-slate-900 border-b border-slate-800 p-6">
          <h1 className="text-2xl font-bold text-white">사용자 관리</h1>
        </div>
        <CommonSidebar />
        <div className="p-6">
          <UserSearchBar
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            onKeyPress={handleKeyPress}
            onClearSearch={handleClearSearch}
            totalUserCount={totalUserCount}
            debouncedSearchTerm={debouncedSearchTerm}
          />

          <DataTable<UserData>
            columns={columns}
            data={[]}
            pagination={undefined}
            loading={false}
            error={error}
          />
        </div>
      </div>
    );
  }

  const rankText = confirmModal.newRank === "expert" ? "전문가" : "일반 사용자";
  const currentRankText =
    confirmModal.currentRank === "expert" ? "전문가" : "일반 사용자";

  return (
    <div className="min-h-screen bg-slate-950 lg:ml-72">
      {/* 헤더 */}
      <div className="bg-slate-900 border-b border-slate-800 p-6 flex justify-between items-center">
        <h1 className="text-2xl font-bold text-white">사용자 관리</h1>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-bold hover:bg-purple-500 transition-colors shadow-lg shadow-purple-900/20"
        >
          <UserPlus size={20} />
          <span className="hidden sm:inline text-base">사용자 생성</span>
        </button>
      </div>
      <CommonSidebar />

      {/* 메인 컨텐츠 */}
      <div className="p-6">
        {/* 검색 영역 */}
        <UserSearchBar
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          onKeyPress={handleKeyPress}
          onClearSearch={handleClearSearch}
          totalUserCount={totalUserCount}
          debouncedSearchTerm={debouncedSearchTerm}
        />

        {/* 유저 리스트 테이블 */}
        <DataTable<UserData>
          columns={columns}
          data={userList}
          pagination={paginationInfo}
          loading={false}
          error={null}
          emptyMessage="검색 결과가 없습니다. 다른 검색어를 입력해주세요."
        />
      </div>

      {/* 히스토리 모달 */}
      {selectedUserId && (
        <HistoryModal
          userHistory={userHistory}
          onClose={handleCloseModal}
          isLoading={isHistoryLoading}
        />
      )}

      {/* 사용자 생성 모달 */}
      <UserCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={async (id, password, nickname) => {
          const success = await createUser(id, password, nickname);
          if (success) getUserDataList(currentPage);
          return success;
        }}
      />

      {/* 등급 변경 확인 모달 */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={handleCloseConfirmModal}
        onConfirm={handleConfirmGradeUpdate}
        title="사용자 등급 변경"
        message={`"${confirmModal.userName}" 사용자의 등급을 "${currentRankText}"에서 "${rankText}"로 변경하시겠습니까?`}
        confirmText="변경"
        cancelText="취소"
        type="warning"
        isLoading={isUpdating}
      />
    </div>
  );
}
